/**
 * Section Formatting Toolbar
 *
 * A custom formatting toolbar that appears at the top of each note section
 * when the section is hovered or focused. Uses BlockNote's FormattingToolbar component
 * styled to fit the section header area.
 */

import React from 'react';
import { FormattingToolbar } from '@blocknote/react';
import { Box, Fade } from '@mui/material';

interface SectionFormattingToolbarProps {
  /**
   * Whether the toolbar is visible
   */
  isVisible: boolean;
}

/**
 * Renders a formatting toolbar at the top of a note section.
 * Must be rendered inside a BlockNoteView context (as a child of EditorView).
 *
 * Positioned absolutely at the top of the section wrapper (which adds padding
 * when toolbar is visible). Fades in/out smoothly.
 */
export const SectionFormattingToolbar: React.FC<SectionFormattingToolbarProps> = ({
  isVisible,
}) => {
  return (
    <Fade in={isVisible} timeout={150}>
      <Box
        sx={{
          position: 'absolute',
          // Position in the reserved space above the editor content
          // Use -32px to stay inside the border container (which has 36px padding)
          top: '-32px',
          // Align with content (account for EditorView's negative margin on both sides)
          left: '2rem',
          right: '2rem',
          height: '32px',
          zIndex: 10,
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          // Bottom border to separate from content
          borderBottom: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.06)',
          pb: 0.5,
          // Style overrides for the FormattingToolbar - make it compact
          '& .bn-formatting-toolbar': {
            border: 'none',
            boxShadow: 'none',
            backgroundColor: 'transparent',
            padding: 0,
            minHeight: 'unset',
          },
          '& .bn-toolbar': {
            border: 'none',
            boxShadow: 'none',
            backgroundColor: 'transparent',
            padding: 0,
            minHeight: 'unset',
          },
          // Make toolbar buttons smaller
          '& .mantine-Group-root': {
            gap: '2px',
          },
          '& .mantine-Button-root, & .mantine-ActionIcon-root': {
            minWidth: '28px',
            minHeight: '28px',
            padding: '4px',
          },
        }}
      >
        <FormattingToolbar />
      </Box>
    </Fade>
  );
};

SectionFormattingToolbar.displayName = 'SectionFormattingToolbar';

